import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { LoadingIcon } from '@prosolve/common-components-ui'
import { FaFolder } from 'react-icons/fa';
import { FiLink, FiEdit2, FiTrash2, FiDownload, FiEye } from 'react-icons/fi';
import ChipButton from './ChipButton';
import { downloadAssetFile } from './downloadUtils';
import { assetTheme as t, formatDate } from './assetTheme';
import { isImageFile, getFileIcon, getFileExtensionLabel, formatBytes } from './fileTypeUtils';

const Panel = styled.div`
  background: ${t.surface};
  border: 1px solid ${t.border};
  border-radius: 14px;
  overflow: hidden;
`

const columns = ({ $isMobileView }) => $isMobileView
  ? 'minmax(0,1fr) 150px'
  : 'minmax(0,1fr) 110px 150px 230px';

const Head = styled.div`
  display: grid;
  grid-template-columns: ${columns};
  align-items: center;
  padding: 0 18px;
  height: 42px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: ${t.faint};
  background: ${t.card2};
  border-bottom: 1px solid ${t.border};
`

const Row = styled.div`
  display: grid;
  grid-template-columns: ${columns};
  align-items: center;
  padding: 0 18px;
  height: 58px;
  border-bottom: 1px solid ${t.border};
  cursor: pointer;
  background: ${({ $isDragOver, $isSelected }) => $isDragOver || $isSelected ? t.teal100 : 'transparent'};
  box-shadow: ${({ $isSelected }) => $isSelected ? `inset 0 0 0 1px ${t.tealDark}` : 'none'};
  opacity: ${({ $isDragging }) => $isDragging ? 0.4 : 1};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${({ $isDragOver, $isSelected }) => $isDragOver || $isSelected ? t.teal100 : t.teal50};
  }
`

const NameCell = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
`

const Thumb = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${t.border};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 17px;
  flex: none;
  background: ${t.card2};
  color: ${t.faint};
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const FileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${t.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FileType = styled.div`
  font-size: 12px;
  color: ${t.faint};
  margin-top: 1px;
`

const Cell = styled.span`
  font-size: 13px;
  color: ${t.muted};
`

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  visibility: hidden;

  ${Row}:hover & {
    visibility: visible;
  }
`

const FileRow = ({ file, isMobileView, isSelectMode, isSelected, onSelect, onPreview, onRename, onDelete }) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const timeoutRef = useRef()

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current)
  }, [])

  const onCopyUrl = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(file.url);
    setIsCopied(true);
    timeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
  }

  return (
    <Row
      $isMobileView={isMobileView}
      $isDragging={isDragging}
      $isSelected={isSelected}
      draggable={true}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', `assetfile:${file.key}`);
        e.dataTransfer.effectAllowed = 'move';
        setIsDragging(true);
      }}
      onDragEnd={() => setIsDragging(false)}
      onClick={() => isSelectMode ? onSelect(file) : onPreview(file)}
    >
      <NameCell>
        <Thumb>
          {
            isImageFile(file.name) ?
            <img src={file.url} alt={file.name} loading='lazy' draggable={false} />
            :
            getFileIcon(file.name)
          }
        </Thumb>
        <div style={{ minWidth: 0 }}>
          <FileName>{file.name}</FileName>
          <FileType>{getFileExtensionLabel(file.name)}</FileType>
        </div>
      </NameCell>
      {!isMobileView && <Cell>{formatBytes(file.size)}</Cell>}
      {!isMobileView && <Cell>{formatDate(file.lastModified)}</Cell>}
      <Actions>
        {
          isSelectMode &&
          <ChipButton $variant='light' $flat={true} title='Preview file' onClick={(e) => { e.stopPropagation(); onPreview(file); }}>
            <FiEye />
          </ChipButton>
        }
        <ChipButton $variant='dark' $flat={true} onClick={onCopyUrl}>
          <FiLink />
          {isCopied ? 'Copied!' : 'Copy URL'}
        </ChipButton>
        {
          !isMobileView &&
          <>
            <ChipButton $variant='light' $flat={true} title='Download file' onClick={(e) => { e.stopPropagation(); downloadAssetFile(file); }}>
              <FiDownload />
            </ChipButton>
            <ChipButton $variant='light' $flat={true} title='Rename file' onClick={(e) => { e.stopPropagation(); onRename(file); }}>
              <FiEdit2 />
            </ChipButton>
            <ChipButton $variant='light' $flat={true} title='Delete file' onClick={(e) => { e.stopPropagation(); onDelete(file); }}>
              <FiTrash2 />
            </ChipButton>
          </>
        }
      </Actions>
    </Row>
  )
}

const FolderRow = ({ folder, isMobileView, onOpen, onRename, onDelete, onDropFile }) => {
  const [isDragOver, setIsDragOver] = useState(false)

  return (
    <Row
      $isMobileView={isMobileView}
      $isDragOver={isDragOver}
      onClick={() => onOpen(folder.path)}
      onDragOver={(e) => { e.preventDefault(); }}
      onDragEnter={(e) => { e.preventDefault(); setIsDragOver(true); }}
      onDragLeave={() => setIsDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragOver(false);
        onDropFile(e, folder.path);
      }}
    >
      <NameCell>
        <Thumb style={{ background: t.surface }}>
          <FaFolder style={{ color: t.yellow }} />
        </Thumb>
        <div style={{ minWidth: 0 }}>
          <FileName>{folder.name}</FileName>
          <FileType>{'Folder'}</FileType>
        </div>
      </NameCell>
      {!isMobileView && <Cell>{'—'}</Cell>}
      {!isMobileView && <Cell>{''}</Cell>}
      <Actions>
        <ChipButton $variant='light' $flat={true} title='Rename folder' onClick={(e) => { e.stopPropagation(); onRename(folder); }}>
          <FiEdit2 />
        </ChipButton>
        <ChipButton $variant='light' $flat={true} title='Delete folder' onClick={(e) => { e.stopPropagation(); onDelete(folder); }}>
          <FiTrash2 />
        </ChipButton>
      </Actions>
    </Row>
  )
}

const EmptyNote = styled.div`
  padding: 40px 0px;
  width: 100%;
  text-align: center;
  color: ${t.faint};
  font-style: italic;
`

export default ({
  isLoading,
  folders = [],
  files = [],
  isMobileView,
  isSelectMode,
  selectedFile,
  onSelectFile,
  onOpenFolder,
  onRenameFolder,
  onDeleteFolder,
  onDropFile,
  onPreviewFile,
  onRenameFile,
  onDeleteFile
}) => {
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
        <LoadingIcon />
      </div>
    )
  }

  if (folders.length === 0 && files.length === 0) {
    return <EmptyNote>{'This folder is empty. Upload files or create a folder to get started.'}</EmptyNote>
  }

  return (
    <Panel>
      <Head $isMobileView={isMobileView}>
        <span>{'Name'}</span>
        {!isMobileView && <span>{'Size'}</span>}
        {!isMobileView && <span>{'Modified'}</span>}
        <span></span>
      </Head>
      {folders.map((folder) => (
        <FolderRow
          key={folder.path}
          folder={folder}
          isMobileView={isMobileView}
          onOpen={onOpenFolder}
          onRename={onRenameFolder}
          onDelete={onDeleteFolder}
          onDropFile={onDropFile}
        />
      ))}
      {files.map((file) => (
        <FileRow
          key={file.key}
          file={file}
          isMobileView={isMobileView}
          isSelectMode={isSelectMode}
          isSelected={!!selectedFile && selectedFile.key === file.key}
          onSelect={onSelectFile}
          onPreview={onPreviewFile}
          onRename={onRenameFile}
          onDelete={onDeleteFile}
        />
      ))}
    </Panel>
  )
}
